import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import PraiseLogo from "@/components/PraiseLogo";

interface RazorpayCheckoutProps {
  planName: string;
  isAnnual: boolean;
  children: React.ReactNode;
  className?: string;
  variant?: "default" | "outline" | "ghost" | "link" | "destructive" | "secondary";
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

const RazorpayCheckout = ({
  planName,
  isAnnual,
  children,
  className,
  variant = "default",
}: RazorpayCheckoutProps) => {
  const { user, refreshProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      setScriptLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    document.body.appendChild(script);
  }, []);

  const handleCheckout = async () => {
    if (!user) {
      toast.error("Please sign in to upgrade your plan");
      return;
    }

    if (!scriptLoaded) {
      toast.error("Payment system loading. Please try again.");
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("create-razorpay-order", {
        body: { planName, isAnnual },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        name: "Praise",
        description: `${planName.charAt(0).toUpperCase() + planName.slice(1)} Plan — ${isAnnual ? "Annual" : "Monthly"}`,
        order_id: data.orderId,
        handler: async (response: any) => {
          try {
            const verifyRes = await supabase.functions.invoke("verify-razorpay-payment", {
              body: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                planName,
              },
            });

            if (verifyRes.error || verifyRes.data?.error) {
              throw new Error(verifyRes.data?.error || "Verification failed");
            }

            await refreshProfile();
            toast.success(`Welcome to ${planName.charAt(0).toUpperCase() + planName.slice(1)}! ${verifyRes.data.credits} credits added.`);
          } catch (err) {
            console.error("Verification error:", err);
            toast.error("Payment received but verification failed. Please contact support.");
          }
        },
        prefill: {
          email: user.email,
          name: user.user_metadata?.full_name || "",
        },
        theme: {
          color: "#7C3AED",
        },
        modal: {
          ondismiss: () => {
            setIsLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response: any) => {
        console.error("Payment failed:", response.error);
        toast.error(response.error.description || "Payment failed. Please try again.");
        setIsLoading(false);
      });
      rzp.open();
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleCheckout}
      disabled={isLoading}
      className={className}
      variant={variant}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Processing...
        </>
      ) : (
        children
      )}
    </Button>
  );
};

export default RazorpayCheckout;
